import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '../lib/api';

const AlertsContext = createContext(null);

export function AlertsProvider({ children }) {
  // Set di keyword per cui l'utente ha già un avviso attivo
  const [alertedKeywords, setAlertedKeywords] = useState(() => {
    try {
      const stored = localStorage.getItem('scanmarket_alerted_keywords');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const saveToStorage = (newSet) => {
    try {
      localStorage.setItem('scanmarket_alerted_keywords', JSON.stringify([...newSet]));
    } catch {}
  };

  const isAlerted = useCallback((keyword) => {
    const kw = keyword?.toLowerCase().trim();
    return kw ? alertedKeywords.has(kw) : false;
  }, [alertedKeywords]);

  const addAlert = useCallback((keyword) => {
    const kw = keyword?.toLowerCase().trim();
    if (!kw) return;
    setAlertedKeywords(prev => {
      const next = new Set(prev);
      next.add(kw);
      saveToStorage(next);
      return next;
    });
  }, []);

  const removeAlert = useCallback(async (keyword) => {
    const kw = keyword?.toLowerCase().trim();
    if (!kw) return;
    setAlertedKeywords(prev => {
      const next = new Set(prev);
      next.delete(kw);
      saveToStorage(next);
      return next;
    });
  }, []);

  const toggleAlert = useCallback(async (keyword, options = {}) => {
    const kw = keyword?.toLowerCase().trim();
    if (!kw) return { success: false };

    if (isAlerted(kw)) {
      // Cerca e cancella tutti gli alert per questa keyword
      try {
        const data = await api.getAlerts();
        const matching = (data.alerts || []).filter(
          a => a.keyword?.toLowerCase().trim() === kw
        );
        await Promise.all(matching.map(a => api.deleteAlert(a._id)));
      } catch {}
      removeAlert(kw);
      return { success: true, action: 'removed' };
    } else {
      // Crea il nuovo alert
      await api.createAlert(keyword, options);
      addAlert(kw);
      return { success: true, action: 'added' };
    }
  }, [isAlerted, addAlert, removeAlert]);

  return (
    <AlertsContext.Provider value={{ isAlerted, toggleAlert, addAlert, removeAlert, alertedKeywords }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  return useContext(AlertsContext);
}
